import { useMemo, type ReactElement, type ReactNode } from 'react';
import { resolveSemanticColor } from '@/theme/semanticTokens';
import { r, s, typographyTokens } from '@/theme/tokens';
import { Box, Surface, useTheme } from '@/ui/primitives';
import { InfoOutlinedIcon, NorthIcon, SouthIcon } from '@/ui/primitives/icons';
import { MiniMetricLineChart } from './MiniMetricLineChart';
import type { MiniMetricChartSummary } from './MiniMetricChartHeader';
import type { MetricsStatusChartPanelDisplayModel } from './useMetricsStatusChartPanelModel';
import type { MiniMetricLineChartDatum } from '@/features/executions/utils/chartData';
import type { MetricCardItem } from '@/features/metrics/components/statusMetricCards';

type TrendDatum = {
  label: string;
  value: number | null;
};

type MetricTrendCardsRowProps = {
  loading: boolean;
  metricCards: MetricCardItem[];
  rangeLabel: string;
  deltaComparisonLabel: string;
  statusChartData: MetricsStatusChartPanelDisplayModel['statusChartData'];
  passRateChartData: MetricsStatusChartPanelDisplayModel['passRateChartData'];
  frequencyChartData: MetricsStatusChartPanelDisplayModel['frequencyChartData'];
  durationChartData: MetricsStatusChartPanelDisplayModel['durationChartData'];
  flakeScoreChartData: MetricsStatusChartPanelDisplayModel['flakeScoreChartData'];
  passRateSummary: MiniMetricChartSummary | null;
  frequencySummary: MiniMetricChartSummary | null;
  durationSummary: MiniMetricChartSummary | null;
  flakeScoreSummary: MiniMetricChartSummary | null | undefined;
};

type TrendDelta = {
  label: string | null;
  value: number | null;
  tone: 'positive' | 'negative' | 'neutral';
};

type TrendCardConfig = {
  id: string;
  chartKey: string;
  title: string;
  valueLabel: string;
  delta: TrendDelta;
  data: TrendDatum[];
  metricType: 'count' | 'duration' | 'percentage';
};

const labelDateFormatter = new Intl.DateTimeFormat('en-GB', {
  month: 'short',
  day: 'numeric',
});
const trendChartLineColor = '#1ea6ad';
const trendCardMinHeight = 86;
const trendCardChartHeight = 34;

const parseDateLabel = (label: string): Date | null => {
  const trimmed = label.trim();
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  const slashMatchWithYear = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(trimmed);
  if (slashMatchWithYear) {
    const [, day, month, year] = slashMatchWithYear;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  const slashMatch = /^(\d{1,2})\/(\d{1,2})$/.exec(trimmed);
  if (slashMatch) {
    const [, day, month] = slashMatch;
    return new Date(new Date().getFullYear(), Number(month) - 1, Number(day));
  }

  return null;
};

const formatDateLabel = (label: string): string => {
  const date = parseDateLabel(label);
  return date ? labelDateFormatter.format(date) : label;
};

const renderNodeLabel = (value: ReactNode): string => {
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  return '-';
};

const buildTotalRunsTrend = (
  statusChartData: MetricsStatusChartPanelDisplayModel['statusChartData'],
): TrendDatum[] => {
  if (!statusChartData) return [];

  return statusChartData.data.map((row) => {
    const total = statusChartData.series.reduce((sum, seriesItem) => {
      const value = row[seriesItem.key];
      return sum + (typeof value === 'number' && Number.isFinite(value) ? value : 0);
    }, 0);

    return {
      label: String(row.label),
      value: total,
    };
  });
};

const buildLineTrend = (
  data: Array<Record<string, string | number | null>> | null,
  key: string,
): TrendDatum[] => {
  if (!data) return [];

  return data.map((row) => {
    const value = row[key];
    return {
      label: String(row.label),
      value: typeof value === 'number' && Number.isFinite(value) ? value : null,
    };
  });
};

const resolveMetricCardDelta = (card: MetricCardItem | undefined): TrendDelta => ({
  label: card
    ? renderNodeLabel(card.percentageDelta ?? card.countDelta ?? card.delta ?? null)
    : null,
  value: null,
  tone: card?.percentageDeltaTone ?? card?.countDeltaTone ?? card?.deltaTone ?? 'neutral',
});

const resolveSummaryDelta = (summary: MiniMetricChartSummary | null): TrendDelta => ({
  label: summary?.deltaLabel ?? null,
  value: summary?.deltaValue ?? null,
  tone: summary?.deltaTone ?? 'neutral',
});

const hasVisibleTrendValue = (data: TrendDatum[]): boolean =>
  data.some((point) => typeof point.value === 'number' && Number.isFinite(point.value));

const buildMiniMetricTrendData = (data: TrendDatum[]): MiniMetricLineChartDatum[] => {
  let carryForwardValue = 0;

  return data.map((point) => {
    if (typeof point.value === 'number' && Number.isFinite(point.value)) {
      carryForwardValue = point.value;
    }

    return {
      label: point.label,
      measuredValue: point.value,
      carryForwardValue,
    };
  });
};

const resolveYMax = (data: TrendDatum[], metricType: TrendCardConfig['metricType']): number => {
  if (metricType === 'percentage') return 106;

  const maxValue = data.reduce(
    (max, point) =>
      typeof point.value === 'number' && Number.isFinite(point.value) && point.value > max
        ? point.value
        : max,
    0,
  );
  if (maxValue <= 0) return 1;
  return maxValue * 1.18;
};

const resolveYMin = (data: TrendDatum[], metricType: TrendCardConfig['metricType']): number => {
  if (metricType === 'percentage') return -6;

  const values = data
    .map((point) => point.value)
    .filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
  if (values.length === 0) return 0;

  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  const range = Math.max(1, maxValue - minValue);
  return Math.min(0, minValue - range * 0.16);
};

const resolveDeltaArrowDirection = (delta: TrendDelta): 'up' | 'down' => {
  if (typeof delta.value === 'number' && Number.isFinite(delta.value)) {
    return delta.value < 0 ? 'down' : 'up';
  }

  return delta.tone === 'negative' ? 'down' : 'up';
};

const MetricTrendCard = ({
  config,
  loading,
  deltaComparisonLabel,
}: {
  config: TrendCardConfig;
  loading: boolean;
  deltaComparisonLabel: string;
}): ReactElement => {
  const theme = useTheme();
  const deltaColor =
    config.delta.tone === 'positive'
      ? resolveSemanticColor(theme, 'success')
      : config.delta.tone === 'negative'
        ? resolveSemanticColor(theme, 'error')
        : theme.app.colors.textMuted;
  const showDeltaIcon =
    config.delta.label !== null &&
    config.delta.label !== '-' &&
    config.delta.value !== 0 &&
    config.delta.tone !== 'neutral';
  const deltaArrowDirection = resolveDeltaArrowDirection(config.delta);
  const yMin = resolveYMin(config.data, config.metricType);
  const yMax = resolveYMax(config.data, config.metricType);
  const chartData = useMemo(() => buildMiniMetricTrendData(config.data), [config.data]);
  const hasData = hasVisibleTrendValue(config.data);

  return (
    <Surface
      padding="none"
      radius="card"
      sx={{
        minHeight: `${trendCardMinHeight}px`,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          height: '100%',
          display: 'grid',
          gridTemplateRows: 'auto minmax(32px, 1fr) auto',
          alignItems: 'stretch',
        }}
      >
        <Box
          sx={{
            minWidth: 0,
            px: s('lg'),
            pt: s('lg'),
            pb: s('xxs'),
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: s('sm'),
              color: theme.app.colors.textMuted,
              fontSize: typographyTokens.caption.fontSize,
              fontWeight: typographyTokens.overline.fontWeight,
              lineHeight: 1.25,
              whiteSpace: 'nowrap',
            }}
          >
            <Box component="span" sx={{ flexShrink: 0, minWidth: 'max-content' }}>
              {config.title}
            </Box>
            <InfoOutlinedIcon
              sx={{ flexShrink: 0, fontSize: 17, color: 'inherit', transform: 'translateY(1px)' }}
            />
          </Box>
        </Box>
        <Box
          sx={{
            minWidth: 0,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'max-content minmax(0, 1fr)' },
            columnGap: s('xs'),
            alignItems: 'stretch',
            minHeight: trendCardChartHeight,
            py: s('md'),
          }}
        >
          <Box
            sx={{
              minWidth: 0,
              px: s('lg'),
              pb: s('xxs'),
              display: 'flex',
              alignItems: 'center',
              color: theme.app.colors.text,
              fontSize: { xs: 20, xl: 22 },
              fontWeight: typographyTokens.overline.fontWeight,
              lineHeight: 1.15,
              fontVariantNumeric: 'tabular-nums',
              whiteSpace: 'nowrap',
            }}
          >
            {loading ? '-' : config.valueLabel}
          </Box>
          <Box
            sx={{
              minWidth: 0,
              px: { xs: s('lg'), md: s('xs') },
              pl: { md: s('none') },
              pb: s('xxs'),
              height: '100%',
              display: 'flex',
              alignItems: 'stretch',
            }}
          >
            {loading || !hasData ? (
              <Box
                sx={{
                  flex: 1,
                  minHeight: trendCardChartHeight,
                  borderRadius: r('sm'),
                  backgroundColor: theme.app.colors.surface,
                }}
              />
            ) : (
              <MiniMetricLineChart
                data={chartData}
                metricKey={config.chartKey}
                color={trendChartLineColor}
                height={trendCardChartHeight}
                marginLeft={2}
                marginRight={8}
                marginTop={4}
                marginBottom={0}
                yAxisWidth={0}
                xAxisHeight={0}
                yAxisMin={yMin}
                yAxisMax={yMax}
                hideXAxisLabels
                hideYAxisLabels
                hideXAxisTicks
                hideYAxisTicks
                hideXAxisLine
                hideYAxisLine
                tooltipLabelFormatter={formatDateLabel}
              />
            )}
          </Box>
        </Box>
        <Box
          sx={{
            gridColumn: { xs: '1', md: '1 / -1' },
            px: s('lg'),
            pb: s('lg'),
            display: 'inline-flex',
            alignItems: 'center',
            gap: s('xs'),
            color: deltaColor,
            fontSize: typographyTokens.body.fontSize,
            fontWeight: typographyTokens.overline.fontWeight,
            lineHeight: 1.2,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {showDeltaIcon ? (
            deltaArrowDirection === 'down' ? (
              <SouthIcon sx={{ fontSize: 16, strokeWidth: 0.5 }} />
            ) : (
              <NorthIcon sx={{ fontSize: 16, strokeWidth: 0.5 }} />
            )
          ) : null}
          {config.delta.label ?? '-'}
          <Box
            component="span"
            sx={{
              color: theme.app.colors.textMuted,
              fontSize: typographyTokens.caption.fontSize,
              fontWeight: typographyTokens.caption.fontWeight,
              ml: s('xxs'),
            }}
          >
            {deltaComparisonLabel}
          </Box>
        </Box>
      </Box>
    </Surface>
  );
};

export const MetricTrendCardsRow = ({
  loading,
  metricCards,
  rangeLabel,
  deltaComparisonLabel,
  statusChartData,
  passRateChartData,
  frequencyChartData,
  durationChartData,
  flakeScoreChartData,
  passRateSummary,
  frequencySummary,
  durationSummary,
  flakeScoreSummary,
}: MetricTrendCardsRowProps): ReactElement => {
  const totalMetricCard = metricCards.find((card) => card.id === 'total');
  const cards = useMemo<TrendCardConfig[]>(
    () => [
      {
        id: 'total-runs',
        chartKey: 'runs',
        title: `Total Runs (${rangeLabel})`,
        valueLabel: renderNodeLabel(totalMetricCard?.count ?? '-'),
        delta: resolveMetricCardDelta(totalMetricCard),
        data: buildTotalRunsTrend(statusChartData),
        metricType: 'count',
      },
      {
        id: 'success-rate',
        chartKey: 'passRate',
        title: `Success Rate (${rangeLabel})`,
        valueLabel: passRateSummary?.valueLabel ?? '-',
        delta: resolveSummaryDelta(passRateSummary),
        data: buildLineTrend(passRateChartData, 'passRate'),
        metricType: 'percentage',
      },
      {
        id: 'frequency',
        chartKey: 'frequency',
        title: `Frequency (${rangeLabel})`,
        valueLabel: frequencySummary?.valueLabel ?? '-',
        delta: resolveSummaryDelta(frequencySummary),
        data: buildLineTrend(frequencyChartData, 'frequency'),
        metricType: 'count',
      },
      {
        id: 'average-duration',
        chartKey: 'duration',
        title: `Avg. Duration (${rangeLabel})`,
        valueLabel: durationSummary?.valueLabel ?? '-',
        delta: resolveSummaryDelta(durationSummary),
        data: buildLineTrend(durationChartData, 'duration'),
        metricType: 'duration',
      },
      {
        id: 'flake-score',
        chartKey: 'flakeScore',
        title: `Flake Score (${rangeLabel})`,
        valueLabel: flakeScoreSummary?.valueLabel ?? '-',
        delta: resolveSummaryDelta(flakeScoreSummary ?? null),
        data: buildLineTrend(flakeScoreChartData ?? null, 'flakeScore'),
        metricType: 'percentage',
      },
    ],
    [
      durationChartData,
      durationSummary,
      flakeScoreChartData,
      flakeScoreSummary,
      frequencyChartData,
      frequencySummary,
      passRateChartData,
      passRateSummary,
      rangeLabel,
      statusChartData,
      totalMetricCard,
    ],
  );

  return (
    <Box
      sx={{
        width: '100%',
        display: 'grid',
        gridTemplateColumns: {
          xs: 'minmax(0, 1fr)',
          xl: 'repeat(5, minmax(0, 1fr))',
        },
        gap: s('lg'),
      }}
    >
      {cards.map((card) => (
        <MetricTrendCard
          key={card.id}
          config={card}
          loading={loading}
          deltaComparisonLabel={deltaComparisonLabel}
        />
      ))}
    </Box>
  );
};
